/**
 * Browser-session helpers so logout / login never reuse another user's
 * in-memory HTTP headers or identity.
 */

export const OPEN_LOGIN_AFTER_LOGOUT_KEY = 'openLoginAfterLogout';

/** Partner-invite popup "seen" flag; session-scoped, cleared with the access tokens. */
export const PARTNER_INVITE_INFO_SEEN_KEY = 'partner_invite_info_seen';

/** `redirectSource` for links opened from emails (see `deepLinkAuthGuard`). */
export const DEEP_LINK_REDIRECT_SOURCE = 'deep-link';

const PRESERVED_LOCAL_STORAGE_KEYS = ['theme', 'lang'] as const;

const AUTH_HEADER_PASSTHROUGH_URL_FRAGMENTS = [
  'verify_otp',
  'change_password',
  'password_reset',
] as const;

export function getStoredAccessToken(): string {
  if (!globalThis.window?.localStorage) {
    return '';
  }
  return localStorage.getItem('webToken') ?? localStorage.getItem('token') ?? '';
}

/**
 * True when the stored access token's `exp` claim (seconds) is in the past.
 * No token, or no readable `exp`, counts as not expired so the server decides.
 */
export function isStoredAccessTokenExpired(now: number = Date.now()): boolean {
  const token = getStoredAccessToken();
  if (!token) {
    return false;
  }
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64)) as { exp?: unknown };
    const exp = payload.exp;
    return typeof exp === 'number' && Number.isFinite(exp) && exp * 1000 <= now;
  } catch {
    return false;
  }
}

export function getUserRoleIdFromAccessToken(): number | null {
  const token = getStoredAccessToken();
  if (!token) {
    return null;
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as {
      user_role_id?: unknown;
    };
    const id = Number(payload.user_role_id);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

/** The logged-in user's role_id, read from the access token. */
export function getRoleIdFromAccessToken(): number | null {
  const token = getStoredAccessToken();
  if (!token) {
    return null;
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { role_id?: unknown };
    const id = Number(payload.role_id);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

/** The logged-in role's display name (role_name) from the access token. */
export function getRoleNameFromAccessToken(): string {
  const token = getStoredAccessToken();
  if (!token) {
    return '';
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { role_name?: unknown };
    return String(payload?.role_name ?? '');
  } catch {
    return '';
  }
}

/** Whether the logged-in role is player — same token check the app uses elsewhere. */
export function isPlayerRoleFromAccessToken(): boolean {
  const token = getStoredAccessToken();
  if (!token) {
    return false;
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { role_name?: unknown };
    return String(payload?.role_name ?? '').toLowerCase() === 'player';
  } catch {
    return false;
  }
}

export function shouldKeepRequestAuthHeader(url: string): boolean {
  return AUTH_HEADER_PASSTHROUGH_URL_FRAGMENTS.some((fragment) =>
    url.includes(fragment),
  );
}

export function reloadCurrentPage(): void {
  globalThis.window?.location.reload();
}

export function reloadAfterNavigation(
  navigation: Promise<boolean> | void,
  reload: () => void = reloadCurrentPage,
): void {
  if (!(navigation instanceof Promise)) {
    return;
  }
  void navigation.then(
    (navigated) => {
      if (navigated) {
        reload();
      }
    },
    () => undefined,
  );
}

export function shouldOpenLoginAfterLogout(): boolean {
  if (!globalThis.window?.sessionStorage) {
    return false;
  }
  const flagged =
    sessionStorage.getItem(OPEN_LOGIN_AFTER_LOGOUT_KEY) === 'true';
  if (flagged) {
    sessionStorage.removeItem(OPEN_LOGIN_AFTER_LOGOUT_KEY);
  }
  return flagged;
}

/** Remembers `url` so a successful login from the popup sends the user back to it. */
export function saveDeepLinkRedirect(url: string): void {
  if (!globalThis.window?.sessionStorage) {
    return;
  }
  sessionStorage.setItem('redirectAfterLogin', url);
  sessionStorage.setItem('redirectSource', DEEP_LINK_REDIRECT_SOURCE);
}

export function hardLogoutAndRedirectHome(
  redirect: (url: string) => void = redirectHomeClearingSpaState,
): void {
  if (!globalThis.window) {
    return;
  }
  clearAuthStoragePreservingPrefs();
  if (globalThis.window.sessionStorage) {
    sessionStorage.setItem(OPEN_LOGIN_AFTER_LOGOUT_KEY, 'true');
  }
  redirect('/home');
}

/** Drops leftover access JWTs so OTP / password-reset headers are not overwritten. */
export function clearStoredAccessTokens(): void {
  if (!globalThis.window?.localStorage) {
    return;
  }
  localStorage.removeItem('token');
  localStorage.removeItem('webToken');
  localStorage.removeItem('role_id');
  localStorage.removeItem('user_role_id');
  localStorage.removeItem('player_role_id');
  localStorage.removeItem('login_field_value');
  localStorage.removeItem(PARTNER_INVITE_INFO_SEEN_KEY);
}

function redirectHomeClearingSpaState(url: string): void {
  const loc = globalThis.window.location;
  const path = loc.pathname.replace(/\/+$/, '') || '/';
  if (path === '/home' || path === '/') {
    loc.reload();
    return;
  }
  loc.assign(url);
}

function clearAuthStoragePreservingPrefs(): void {
  const preserved: Record<string, string> = {};
  if (globalThis.window.localStorage) {
    for (const key of PRESERVED_LOCAL_STORAGE_KEYS) {
      const value = localStorage.getItem(key);
      if (value) {
        preserved[key] = value;
      }
    }
    localStorage.clear();
    for (const [key, value] of Object.entries(preserved)) {
      localStorage.setItem(key, value);
    }
  }
  globalThis.window.sessionStorage?.clear();
}
